

import { useEffect, useState } from "react";
import Services from "../../services/services";
import { Key, Package, Trash2, Plus, Check, PlusIcon } from "lucide-react";
import { toast } from "react-toastify";
import { RoutePages, toastCustom } from "../../ultils";
import SubscriptionCard from "../../components/SubscriptionCard";
import Title from "../../components/Title";

function MyAccessToken() {
    const [loadingAPIKeys, setLoadingAPIKeys] = useState(false);
    const [apiKeys, setApiKeys] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [newKeySecret, setNewKeySecret] = useState(null);
    // Manage Keys and Subs
    const [selectedKey, setSelectedKey] = useState(null);
    const [selectedSub, setSelectedSub] = useState(null);

    // Fetch Access Tokens
    const fetchAPIKeys = async () => {
        const res = await Services.APIKey.get();
        const data = await res.json();
        setApiKeys(data?.data || []);
    };

    // Fetch Subscriptions
    const fetchSubscriptions = async () => {
        const res = await Services.UserAuth.getSubscriptions();
        const data = await res.json();
        console.log("Fetched Subscriptions:", data);
        setSubscriptions(data?.data || []);
    };

    useEffect(() => {
        fetchAPIKeys();
        fetchSubscriptions();
    }, []);

    useEffect(() => {
        if (selectedKey)
            setSelectedKey((prev) => apiKeys.find(key => key.secret === selectedKey.secret) || prev);
    }, [apiKeys, selectedKey]);

    useEffect(() => {
        if (selectedSub)
            setSelectedSub((prev) => subscriptions.find(sub => sub.id === selectedSub.id) || prev);
    }, [subscriptions, selectedSub]);

    // CRUD Access Token
    const handleCreateKey = async () => {
        setLoadingAPIKeys(true);
        try {
            const res = await Services.APIKey.create(newKeySecret);
            const data = await res.json();
            if (res.ok && data.success) {
                fetchAPIKeys();
                toast.success("Access Token created successfully!");
            } else {
                toast.error(`code(${res.status}): ${data.error ?? ""}`);
            }
            setNewKeySecret(null);
        } catch (error) {
            toast.error(`Error creating Access Token: ${error.message}`);
        }
        setLoadingAPIKeys(false);
    };
    const handleUpdateKey = async (key) => {
        setLoadingAPIKeys(true);
        try {
            const res = await Services.APIKey.update(key.secret, key.name);
            const data = await res.json();
            if (res.ok && data.success) {
                fetchAPIKeys();
                toast.success("Access Token updated successfully!");
            } else {
                toast.error(`code(${res.status}): ${data.error ?? ""}`);
            }
        } catch (error) {
            toast.error(`Error updating Access Token: ${error.message}`);
        }
        setLoadingAPIKeys(false);
    };
    const handleDeleteKey = async (key) => {
        toastCustom(
            `Are you sure you want to delete this Access Token?\n${key?.secret}`,
            [
                {
                    label: "Confirm",
                    className: "bg-red-500 hover:bg-red-700 text-white",
                    onClick: async () => {
                        setLoadingAPIKeys(true);
                        try {
                            const res = await Services.APIKey.delete(key.secret);
                            const data = await res.json();
                            if (res.ok && data.success) {
                                fetchAPIKeys();
                                setSubscriptions((prev) =>
                                    prev.map((sub) => ({ ...sub, mappings: sub.mappings.filter(m => m.secret !== key.secret) }))
                                );
                                toast.success("Access Token deleted successfully!");
                            } else {
                                toast.error(`code(${res.status}): ${data.error ?? ""}`);
                            }
                        } catch (error) {
                            toast.error(`Error deleting Access Token: ${error.message}`);
                        }
                        setLoadingAPIKeys(false);
                    }
                },
                {
                    label: "Cancel",
                    className: "bg-gray-500 hover:bg-gray-700 text-white",
                    onClick: () => toast.dismiss()
                }
            ]
        );
    };

    const handleCopySecret = (secret) => {
        navigator.clipboard.writeText(secret)
            .then(() => {
                toast.success("Copied Access Token secret!");
            })
            .catch(() => {
                toast.error("Failed to copy Access Token secret.");
            });
    };

    const handleChangeMapping = async (sub, key, will_selected) => {
        // setLoading(true);
        try {
            let res, data;
            if (!will_selected) {
                res = await Services.APIKey.revokePermission(key.secret, sub.id);
            } else {
                res = await Services.APIKey.grantPermission(key.secret, sub.id);
            }
            data = await res.json();
            if (res.ok && data.success) {
                toast.success("Permission updated successfully!");
                setSubscriptions((prev) => prev.map((s) => (s.id === sub.id ? { ...s, mappings: data?.data?.subscription?.mappings } : s)));
                setApiKeys((prev) => prev.map((k) => (k.secret === key.secret ? { ...k, mappings: data?.data?.api_key?.mappings } : k)));
            } else {
                toast.error(`code(${res.status}): ${data.error ?? ""}`);
            }
        } finally {
            // setLoading(false);
        }
    }

    const REACT_APP_BASE_API_URL = process.env.REACT_APP_BASE_API_URL ?? "";
    const API_CHECK_SUBSCRIPTION_URL = REACT_APP_BASE_API_URL + "/user/apikey/subscriptions?access_token=YOUR_ACCESS_TOKEN";

    return (
        <div className="p-4 sm:p-8 bg-gray-50 min-h-screen">
            <Title title="Access Tokens" page={RoutePages.MY_ACCESS_TOKEN} />
            {/* Access Token */}
            <div className={`${loadingAPIKeys ? "opacity-50 pointer-events-none" : ""}`}>
                <div className="flex flex-row">
                    <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-blue-700 w-full">
                        <Key className="w-6 h-6" /> My Access Tokens
                    </h2>
                    <div className="text-xl text-gray-600 italic underline w-full items-begin justify-end flex">
                        <a href="https://docs.google.com/document/d/1PRAUZn7JhEAEmY-JahtbQJ_8vsGsK8U7/edit?usp=sharing&ouid=113491512741458417627&rtpof=true&sd=true">
                            *How to use this page*
                        </a>
                    </div>
                </div>
                <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center">
                    <input
                        className="border rounded px-3 py-2 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="(Optional) Access Token Secret"
                        value={newKeySecret}
                        onChange={(e) => setNewKeySecret(e.target.value)}
                    />
                    <button
                        onClick={handleCreateKey}
                        className={`flex items-center gap-2 px-4 py-2 rounded bg-blue-500 text-white font-semibold hover:bg-blue-600 transition disabled:opacity-50`}
                    >
                        <Plus className="w-4 h-4" /> Create {!newKeySecret ? "random" : "an"} Access Token
                    </button>
                </div>
                <div className="overflow-x-auto mb-10">
                    <table className="min-w-full table-auto border-separate border-spacing-0 bg-white rounded-xl shadow-lg">
                        <thead>
                            <tr className="bg-blue-200 text-blue-800">
                                <th className="p-2 border-b text-center text-lg font-semibold rounded-tl-xl">Access Token</th>
                                <th className="p-2 border-b text-center text-lg font-semibold">Note</th>
                                <th className="p-2 border-b text-center text-lg font-semibold">Subscriptions</th>
                                <th className="p-2 border-b text-center text-lg font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {apiKeys.map((key, idx) => {
                                let selected = selectedSub?.mappings?.some((mapping) => mapping.secret === key.secret);
                                return <tr key={key.id} className={`transition hover:bg-blue-50 ${idx === 0 ? 'rounded-t-xl' : ''} ${idx === apiKeys.length - 1 ? 'rounded-b-xl' : ''}`}>
                                    <td className="border-b p-2 font-mono text-sm text-center align-middle">
                                        <div
                                            className="break-all flex flex-row items-center justify-center w-full hover:bg-blue-100 cursor-pointer rounded-lg p-1 relative group"
                                            onClick={() => handleCopySecret(key.secret)}
                                        >
                                            {key.secret}
                                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded bg-black text-white text-xs opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
                                                Click to copy
                                            </span>
                                        </div>
                                    </td>
                                    <td className="border-b p-2 text-center align-middle">
                                        <input className="border rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            placeholder="Note..."
                                            value={key.name || ""}
                                            onChange={(e) => { setApiKeys((prev) => prev.map((k) => k.secret === key.secret ? { ...k, name: e.target.value } : k)); }}
                                            onBlur={() => handleUpdateKey(key)}
                                        />
                                    </td>
                                    <td className="border-b p-2 text-center align-middle">
                                        {selectedSub ? (
                                            <div
                                                className={`p-1 w-full flex justify-center rounded-lg border transition-all duration-200 shadow-sm cursor-pointer
                                            ${selected ? "bg-green-500 text-white border-green-600 hover:bg-green-600" : "bg-gray-100 text-blue-700 border-blue-300 hover:bg-blue-200"}`}
                                                onClick={() => handleChangeMapping(selectedSub, key, !selected)}
                                            >
                                                {selected ? <Check /> : <PlusIcon />}
                                            </div>
                                        ) : (
                                            <div
                                                className={`flex items-center justify-center gap-2 px-1 py-1 rounded-lg transition cursor-pointer border border-blue-200 text-blue-700 shadow-sm ${selectedKey?.secret === key?.secret ? "bg-blue-300 border-blue-400" : "bg-white hover:bg-blue-50"}`}
                                                onClick={() => setSelectedKey(selectedKey?.secret === key?.secret ? null : key)}
                                            >
                                                <span className="inline-flex items-center gap-1 font-semibold">
                                                    Edit
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${key.mappings?.length > 0 ? "bg-blue-500 text-white" : "bg-gray-300 text-gray-600"}`}>
                                                        {key.mappings?.length || 0}
                                                    </span>
                                                </span>
                                            </div>)}
                                    </td>
                                    <td className="border-b p-2 text-center align-middle">
                                        <div className="flex gap-2 justify-center">
                                            <button
                                                onClick={() => handleDeleteKey(key)}
                                                className="flex items-center gap-1 px-1 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition disabled:opacity-50"
                                            >
                                                <Trash2 className="w-8 h-6" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Subscription WrapPanel */}
            <div>
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-green-700">
                    <Package className="w-6 h-6" /> My Subscriptions
                </h2>
                <div className="mb-4">
                    <span className="italic text-gray-600">*You can check your subscription information by HTTP GET <span onClick={() => window.open(API_CHECK_SUBSCRIPTION_URL, "_blank")} className="text-blue-500 hover:cursor-pointer">{API_CHECK_SUBSCRIPTION_URL}</span></span>
                </div>
                <div className="flex flex-wrap gap-6 justify-start">
                    {subscriptions.length === 0 ? (
                        <div className="text-gray-400 text-center w-full">No subscriptions found.</div>
                    ) : (
                        subscriptions.map((sub) => {
                            let selected = selectedKey?.mappings?.some((mapping) => mapping.subscription_id === sub.id);
                            return (<div className={`p-1 w-120 border-2 rounded-lg ${selected ? "border-blue-500 bg-blue-100" : ""}`} key={sub.id}>
                                {selectedKey ? (
                                    <div
                                        className={`p-1 w-full flex justify-center rounded-lg border transition-all duration-200 shadow-sm cursor-pointer
                                            ${selected ? "bg-green-500 text-white border-green-600 hover:bg-green-600" : "bg-gray-100 text-blue-700 border-blue-300 hover:bg-blue-200"}`}
                                        onClick={() => handleChangeMapping(sub, selectedKey, !selected)}
                                    >
                                        {selected ? <Check /> : <PlusIcon />}
                                    </div>
                                ) : (
                                    <div
                                        className={`flex items-center justify-center gap-2 px-1 py-1 rounded-lg transition cursor-pointer border border-blue-400 shadow-sm ${selectedSub?.id === sub?.id ? "bg-blue-400 border-blue-500" : "hover:bg-blue-200"}`}
                                        onClick={() => setSelectedSub(selectedSub?.id === sub.id ? null : sub)}
                                    >
                                        <span className="inline-flex items-center gap-1 text-blue-700 font-semibold">
                                            Add to Access Token
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${sub.mappings?.length > 0 ? "bg-blue-500 text-white" : "bg-gray-300 text-gray-600"}`}>
                                                {sub.mappings?.length || 0}
                                            </span>
                                        </span>
                                    </div>
                                )
                                }
                                <SubscriptionCard subData={sub} />
                            </div>)
                        }
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

export default MyAccessToken;
